import { Colors, Metrics } from "../../resources";
import { Orientation, SIconType, STagType, Tag } from "../../types";
import React, { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useQueryParams, useSettings, useTags } from "../../hooks";

import { Analytics } from "../../library";
import SFilterButton from "../molecules/SFilterButton";
import SSpacer from "../atoms/SSpacer";
import STagsBar from "../molecules/STagsBar";
import STagsScrolledView from "./STagsScrolledView";
import STextInput from "../atoms/STextInput";
import _ from "lodash";

interface IProps {
  defaultFilterView: boolean;
  setOpensize: (openSize: number) => void;
  onToggleFilterView: (impersonate: boolean) => void;
}

const SSearchBar: React.FC<IProps> = ({
  defaultFilterView,
  setOpensize,
  onToggleFilterView,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultFilterView);
  const [search, setSearch] = useState<string>("");
  const { settings, writeSetting } = useSettings();

  const { tags, addTags, categories, setUnselected } = useTags();
  const { search: searchQuery } = useQueryParams();

  const maxHeight = categories.length * 36;

  const delayedStartSearch = _.debounce(
    (text: string) => startSearch(text),
    500
  );

  const clearTags = () => {
    Object.keys(tags).forEach((key) => {
      if (tags[key].selected) {
        setUnselected(key);
      }
    });
  };

  window.onbeforeunload = () => {
    clearTags();
  };

  useEffect(() => {
    writeSetting({
      search: "",
    });
    clearTags();
  }, []);

  useEffect(() => {
    onToggleFilterView(isOpen);
    setOpensize(isOpen ? maxHeight : 0);
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery) {
      writeSetting({
        search: searchQuery.toString(),
      });
      Analytics.track("Portfolio Search", { value: searchQuery.toString() });
      setSearch(searchQuery.toString());
    }
  }, [searchQuery]);

  const startSearch = (text: string) => {
    console.log("Search:", text);
    writeSetting({
      search: text,
    });
    Analytics.track("Portfolio Search", { value: text });
  };

  const onChangeText = (text: string) => {
    setSearch(text);
    delayedStartSearch(text);
  };

  const resetSearch = () => {
    if (settings?.search || settings?.search === "") {
      writeSetting({
        search: "",
      });
      setSearch("");
    }
  };

  const onPress = () => {
    setIsOpen((isOpen) => !isOpen);
  };

  const getSelectedTagsCount = () => {
    return Object.keys(tags)
      .map((id: string) => tags[id])
      .reduce(
        (accumulator: number, tag: Tag) =>
          tag.selected ? accumulator + 1 : accumulator,
        0
      );
  };

  return (
    <View style={styles.container}>
      <View style={[styles.menu, isOpen && styles.menuOpen]}>
        <STextInput
          type={SIconType.Search}
          width={240}
          placeholder="Search"
          onChangeText={onChangeText}
          value={search}
          accessoryIcon
          accessoryIconType={SIconType.Clear}
          onAccessoryIconPress={resetSearch}
        />
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding8} />
        <SFilterButton
          iconType={SIconType.Filters}
          onPress={onPress}
          count={getSelectedTagsCount()}
          isOpen={isOpen}
          disabled={Object.keys(tags).length === 0}
        />
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding8} />
        <STagsBar
          tags={tags}
          type={STagType.Closed}
          onClear={clearTags}
          hasClearButton
        />
      </View>
      <STagsScrolledView
        isOpen={isOpen}
        maxHeight={maxHeight}
        resetSearch={resetSearch}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    backgroundColor: Colors.white,
  },
  menu: {
    flexDirection: "row",
    height: Metrics.menuLargeHeight,
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    paddingHorizontal: Metrics.padding32,
  },
  menuOpen: {
    shadowColor: Colors.shadow,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowRadius: 5,
    shadowOpacity: 0.4,
    elevation: 5,
    zIndex: 1,
  },
});

export default SSearchBar;
